import { useEffect, useState } from "react";
import { api } from "../api";
import { ErrorNote, fmtCredits, fmtUsd, Icon, Spinner, timeAgo, useLoad } from "../components/bits";
import { Fold } from "../components/Fold";
import { Shell } from "../components/Shell";
import { isCountName } from "../credits";
import { emailReceipt, loadPurchases, type Purchase } from "../receipts";
import { groupDirectorCalls, ledgerSummary } from "../foldtext";
import { CHECKOUT_NOTE } from "../legal/guard";
import { director } from "../director";
import { navigate, useLocation } from "../router";
import { set, toast, useRefresh, useStore } from "../store";

export function CreditsScreen() {
  const { query } = useLocation();
  const ok = query.get("ok") === "1";
  const cancelled = query.get("cancelled") === "1" || query.get("canceled") === "1";
  const tick = useRefresh("credits");
  const { data, error, loading, reload } = useLoad(() => api.credits(), [tick, ok]);
  const { data: packData, error: packsError } = useLoad(() => api.packs(), []);
  const packs = packData?.packs;
  const canBuy = packData?.enabled !== false;
  const storeBalance = useStore((s) => s.balance);
  const [buying, setBuying] = useState<string | null>(null);

  useEffect(() => {
    if (data) set({ balance: data.balance });
  }, [data]);
  useEffect(() => {
    if (ok) director.context("The user just came back from Stripe Checkout and their credits were added.");
  }, [ok]);

  const balance = data?.balance ?? storeBalance;
  const latestBuy = ok ? data?.ledger.find((l) => l.delta > 0) : undefined;
  // v4: a Director call is one line in the history (its minute-by-minute entries added up)
  const history = data ? groupDirectorCalls(data.ledger) : undefined;

  const buy = async (packId: string) => {
    setBuying(packId);
    try {
      const { url } = await api.checkout(packId);
      if (!url) throw new Error("Checkout didn't return a link.");
      // Stripe Checkout is another origin: a full redirect. It comes back to /api/credits/confirm, then /credits?ok=1
      if (url.startsWith("/")) navigate(url);
      else location.assign(url);
    } catch (e) {
      toast(`Couldn't open checkout. ${(e as Error).message}`, "bad");
      setBuying(null);
    }
  };

  return (
    <Shell>
      <div className="page credits">
        <header className="page-head">
          <h1 className="page-title">Credits</h1>
          <p className="page-lede">Credits pay for what your Director makes: scene pictures, voiceover, rendering and talk time. Every video shows its price before anything is made, and nothing is charged until you say go.</p>
        </header>

        {ok && (
          <div className="banner banner-good" role="status">
            <Icon name="check" size={20} />
            <p>
              <strong>Payment received.</strong> {latestBuy ? `${fmtCredits(latestBuy.delta)} credits were added.` : "Your credits were added."}
            </p>
            <button type="button" className="icon-btn" aria-label="Dismiss" onClick={() => navigate("/credits", { replace: true })}>
              <Icon name="close" size={18} />
            </button>
          </div>
        )}
        {cancelled && (
          <div className="banner" role="status">
            <p>Checkout was cancelled. You weren't charged.</p>
          </div>
        )}

        <div className="credits-grid">
          <section className="balance-hero" aria-label="Balance">
            <p className="panel-label">Balance</p>
            <p className="balance-big">{fmtCredits(balance)}</p>
            <p className="muted">Spent when something is made for you: a script, pictures, clips, sounds, music, the video, and calls with your Director.</p>
          </section>

          <section className="packs" aria-labelledby="packs-h">
            <h2 id="packs-h" className="section-title">
              Buy credits
            </h2>
            {packsError && <ErrorNote text={`Couldn't load the packs. ${packsError}`} />}
            {!packs && !packsError && <Spinner label="Loading packs" />}
            <ul className="pack-list">
              {packs?.map((p) => (
                <li key={p.id} className="pack">
                  <div className="pack-info">
                    <p className="pack-credits">
                      {fmtCredits(p.credits)} <span>credits</span>
                    </p>
                    <p className="pack-name">
                      {/* a top-up's name is just its credit count, already said in big type above */}
                      {!isCountName(p.name) && p.name}
                      {p.note && <span>{p.note}</span>}
                    </p>
                  </div>
                  <button data-action="credits.buy" type="button" className="btn btn-primary" onClick={() => void buy(p.id)} disabled={!!buying || !canBuy}>
                    {buying === p.id ? "Opening" : `Buy for ${fmtUsd(p.usd)}`}
                  </button>
                </li>
              ))}
            </ul>
            <p className="test-note">
              {canBuy ? "Payments run in Stripe test mode. Use card 4242 4242 4242 4242, any future date and any CVC." : "Buying credits is switched off on this server (no Stripe key yet)."}
            </p>
            <p className="muted pack-terms">
              {CHECKOUT_NOTE.before} <a href={CHECKOUT_NOTE.link.path}>{CHECKOUT_NOTE.link.text}</a> {CHECKOUT_NOTE.after}
            </p>
          </section>

          {/* v4 (V4-P2): the history is a Fold whose row shows the latest entry */}
          <Fold id="credits.history" title="History" count={history?.length || undefined} summary={history ? ledgerSummary(history) : ""} holds={["#ledger-h"]} className="ledger">
            <h2 id="ledger-h" className="section-title sr-only">
              History
            </h2>
            {error && <ErrorNote text={`Couldn't load your history. ${error}`} retry={reload} />}
            {loading && !data && <Spinner label="Loading" />}
            {history && !history.length && <p className="muted">Nothing yet. Credits you buy and spend show up here.</p>}
            {!!history?.length && (
              <ul className="ledger-list">
                {history.slice(0, 30).map((l) => (
                  <li key={l.id}>
                    <span className="ledger-reason">
                      {l.reason === "grant" ? "From the CAO Studio team" : l.reason}
                      <small>{timeAgo(l.createdAt)}</small>
                    </span>
                    <span className={`ledger-delta${l.delta > 0 ? " is-plus" : ""}`}>
                      {l.delta > 0 ? "+" : "−"}
                      {fmtCredits(Math.abs(l.delta))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Fold>

          <Purchases />
        </div>
      </div>
    </Shell>
  );
}

/** v4 (D6 parity with receipts / email_receipt): the packs they bought, each with "Email receipt". */
function Purchases() {
  const tick = useRefresh("credits");
  const { data, error, reload } = useLoad(loadPurchases, [tick]);
  const [sending, setSending] = useState<string | null>(null);
  const [sent, setSent] = useState<Set<string>>(new Set());
  if (!data?.length && !error) return null;
  const send = async (p: Purchase) => {
    setSending(p.id);
    try {
      if (!(await emailReceipt(p.id))) throw new Error("The email didn't go out. Try again in a minute.");
      setSent((s) => new Set(s).add(p.id));
      toast("Receipt sent to your email.", "good");
      director.context(`The user emailed themselves the receipt for ${p.packName} (${dateOf(p.at)}) by tapping.`);
    } catch (e) {
      toast(`Couldn't send the receipt. ${(e as Error).message}`, "bad");
    } finally {
      setSending(null);
    }
  };
  return (
    <Fold id="credits.purchases" title="Purchases" count={data?.length || undefined} summary={data?.[0] ? `Latest: ${dateOf(data[0].at)}` : ""} holds={["credits.receipts", "#purchases-h"]} className="ledger purchases">
      <h2 id="purchases-h" className="section-title sr-only">
        Purchases
      </h2>
      {error && <ErrorNote text={`Couldn't load your purchases. ${error}`} retry={reload} />}
      {!!data?.length && (
        <ul className="ledger-list purchase-list" data-help="credits.receipts">
          {data.slice(0, 20).map((p) => (
            <li key={p.id}>
              <span className="ledger-reason">
                {fmtCredits(p.credits)} credits{!isCountName(p.packName) && <> · {p.packName}</>}
                <small>
                  {dateOf(p.at)} · {fmtUsd(p.usd)}
                </small>
              </span>
              <button type="button" className="btn btn-quiet btn-small" data-action="credits.email-receipt" disabled={sending !== null} onClick={() => void send(p)}>
                {sending === p.id ? "Sending" : sent.has(p.id) ? "Sent" : "Email receipt"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Fold>
  );
}

const dateOf = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
